import { useCallback, useEffect, useMemo, useState } from 'react';

import { listOwnedShares } from 'src/apis/share';
import { OwnedShareSummary, ShareResourceTypeApi } from 'src/types/share';

const getOwnedResourceCounts = (
  shares: OwnedShareSummary[],
  resourceType: ShareResourceTypeApi
): Map<string, number> => {
  const counts = new Map<string, number>();
  shares
    .filter((share) => share.resourceType === resourceType)
    .forEach((share) => {
      counts.set(String(share.resourceId), share.organizationCount ?? 0);
    });
  return counts;
};

const getOwnedFolderCounts = (
  shares: OwnedShareSummary[],
  resourceType: 'DASHBOARD_FOLDER' | 'CAMPAIGN_FOLDER'
): Map<string, number> => {
  const counts = new Map<string, number>();
  shares
    .filter((share) => share.resourceType === resourceType)
    .forEach((share) => {
      const count = share.organizationCount ?? 0;
      counts.set(String(share.resourceId), count);
      if (share.resourceName) {
        counts.set(share.resourceName, count);
      }
    });
  return counts;
};

export default function useOwnedShares() {
  const [shares, setShares] = useState<OwnedShareSummary[]>([]);
  const [isReady, setIsReady] = useState(false);

  const refreshOwnedShares = useCallback(async () => {
    try {
      setShares(await listOwnedShares());
    } catch {
      setShares([]);
    } finally {
      setIsReady(true);
    }
  }, []);

  useEffect(() => {
    refreshOwnedShares();
  }, [refreshOwnedShares]);

  const sharedOutDashboardIds = useMemo(
    () => getOwnedResourceCounts(shares, 'DASHBOARD'),
    [shares]
  );
  const sharedOutDashboardFolderKeys = useMemo(
    () => getOwnedFolderCounts(shares, 'DASHBOARD_FOLDER'),
    [shares]
  );
  const sharedOutCampaignIds = useMemo(
    () => getOwnedResourceCounts(shares, 'CAMPAIGN'),
    [shares]
  );
  const sharedOutCampaignFolderKeys = useMemo(
    () => getOwnedFolderCounts(shares, 'CAMPAIGN_FOLDER'),
    [shares]
  );

  return {
    shares,
    isReady,
    refreshOwnedShares,
    sharedOutDashboardIds,
    sharedOutDashboardFolderKeys,
    sharedOutCampaignIds,
    sharedOutCampaignFolderKeys,
  };
}
